import mapboxgl from "mapbox-gl";
import { GeoJSONFeatureCollection } from "../types";

export const initializeMap = (container: HTMLDivElement): mapboxgl.Map => {
    const map = new mapboxgl.Map({
        container,
        style: "mapbox://styles/mapbox/light-v11",
        center: [9.3, 47.4],
        zoom: 7,
    });
    map.addControl(new mapboxgl.NavigationControl(), "bottom-right");
    return map;
};

export const addRoutesLayer = (
    map: mapboxgl.Map,
    fetchedRoutes: GeoJSONFeatureCollection | null,
    position: 'left' | 'right' = 'right'
) => {
    if (!fetchedRoutes || !fetchedRoutes.features.length) return;  // Add length check

    if (map.getLayer("routes-layer")) map.removeLayer("routes-layer");
    if (map.getSource("routes")) map.removeSource("routes");

    const filterId = `route-filter-group-${position}`;
    let filterGroup = document.getElementById(filterId);

    if (filterGroup) {
        filterGroup.innerHTML = '';
    } else {
        filterGroup = document.createElement('div');
        filterGroup.id = filterId;
        filterGroup.style.position = 'absolute';
        filterGroup.style.top = '10px';
        filterGroup.style[position] = '10px';
        filterGroup.style.backgroundColor = 'white';
        filterGroup.style.padding = '10px';
        filterGroup.style.borderRadius = '4px';
        filterGroup.style.zIndex = '1';
        filterGroup.style.boxShadow = '0 0 10px rgba(0,0,0,0.1)';

        map.getContainer().appendChild(filterGroup);
    }

    const providersWithColors = fetchedRoutes.features.reduce<Map<string, string>>(
        (map, feature) => {
            if (!map.has(feature.properties.provider)) {
                map.set(feature.properties.provider, feature.properties.color);
            }
            return map;
        },
        new Map()
    );

    providersWithColors.forEach((color, provider) => {
        const layerID = `provider-${provider}-${position}`;
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.id = layerID;
        input.checked = true;
        filterGroup?.appendChild(input);

        const label = document.createElement('label');
        label.setAttribute('for', layerID);
        label.textContent = provider;
        label.style.color = color;
        label.style.fontWeight = 'bold';
        filterGroup?.appendChild(label);
        filterGroup?.appendChild(document.createElement('br'));

        input.addEventListener('change', () => {
            const hiddenRoutes = Array.from(providersWithColors.keys()).filter(p => {
                const checkbox = document.getElementById(`provider-${p}-${position}`) as HTMLInputElement;
                return checkbox ? !checkbox.checked : false;  // Handle case where checkbox doesn't exist
            });
            if (map.getLayer("routes-layer")) {
                map.setFilter("routes-layer", [
                    "!",
                    ["in", ["get", "provider"], ["literal", hiddenRoutes]],
                ]);
            }
        });
    });

    map.addSource("routes", {
        type: "geojson",
        data: fetchedRoutes,
    });

    map.addLayer({
        id: "routes-layer",
        type: "line",
        source: "routes",
        layout: {
            "line-join": "round",
            "line-cap": "round",
        },
        paint: {
            "line-color": [
                "interpolate",
                ["linear"],
                ["line-progress"],
                0,
                ["get", "color"],
                1,
                "red",
            ],
            "line-width": 4,
            "line-opacity": Math.max(0.1, Math.min(0.8, 35 / fetchedRoutes.features.length)),
            "line-blur": 3,
        },
    });

    const bounds = new mapboxgl.LngLatBounds();
    fetchedRoutes.features.forEach((feature) => {
        feature.geometry.coordinates.forEach((coord) => {
            bounds.extend(coord as [number, number]);
        });
    });
    map.fitBounds(bounds, { padding: 50 });

    addRouteInteractivity(map);
};

const addRouteInteractivity = (map: mapboxgl.Map) => {
    map.on("click", "routes-layer", (e) => {
        if (!e.features) return;
        const coordinates = e.lngLat;
        const properties = e.features[0].properties;

        new mapboxgl.Popup()
            .setLngLat(coordinates)
            .setHTML(
                `<strong>${properties?.route_name}</strong><br/>Provider: ${properties?.provider}`
            )
            .addTo(map);
    });

    map.on("mouseenter", "routes-layer", () => {
        map.getCanvas().style.cursor = "pointer";
    });

    map.on("mouseleave", "routes-layer", () => {
        map.getCanvas().style.cursor = "";
    });
};

export const addSchoolsLayer = (map: mapboxgl.Map, fetchedSchools: GeoJSONFeatureCollection | null) => {
    if (!fetchedSchools || !fetchedSchools.features.length) return;  // Add length check

    // Add legend
    let legend = document.getElementById('schools-legend');
    if (!legend) {
        legend = document.createElement('div');
        legend.id = 'schools-legend';
        legend.style.position = 'absolute';
        legend.style.bottom = '35px';
        legend.style.left = '10px';
        legend.style.backgroundColor = 'white';
        legend.style.padding = '10px';
        legend.style.borderRadius = '4px';
        legend.style.zIndex = '1';
        legend.style.boxShadow = '0 0 10px rgba(0,0,0,0.1)';

        // Add legend content
        const title = document.createElement('div');
        title.style.fontWeight = 'bold';
        title.style.marginBottom = '5px';
        title.textContent = 'Legend';
        legend.appendChild(title);

        // Add school symbol
        const symbolContainer = document.createElement('div');
        symbolContainer.style.display = 'flex';
        symbolContainer.style.alignItems = 'center';
        symbolContainer.style.gap = '5px';

        const symbol = document.createElement('div');
        symbol.style.width = '10px';
        symbol.style.height = '10px';
        symbol.style.backgroundColor = '#FF0000';
        symbol.style.borderRadius = '50%';

        const label = document.createElement('span');
        label.textContent = 'Schools';

        symbolContainer.appendChild(symbol);
        symbolContainer.appendChild(label);
        legend.appendChild(symbolContainer);

        map.getContainer().appendChild(legend);
    }

    const addLayers = () => {
        try {
            ['schools-polygons-layer', 'schools-points-layer'].forEach(layerId => {
                if (map.getLayer(layerId)) {
                    map.removeLayer(layerId);
                }
            });

            ['schools-points', 'schools-polygons'].forEach(sourceId => {
                if (map.getSource(sourceId)) {
                    map.removeSource(sourceId);
                }
            });

            // Separate features into points and polygons and add them to separate sources
            const pointFeatures = fetchedSchools.features.filter(
                (feature: { geometry: { type: string; }; }) => feature.geometry.type === "Point"
            );

            const polygonFeatures = fetchedSchools.features.filter(
                (feature: { geometry: { type: string; }; }) => feature.geometry.type === "Polygon" || feature.geometry.type === "MultiPolygon"
            );

            map.addSource("schools-points", {
                type: "geojson",
                data: {
                    type: "FeatureCollection",
                    features: pointFeatures,
                },
            });

            map.addSource("schools-polygons", {
                type: "geojson",
                data: {
                    type: "FeatureCollection",
                    features: polygonFeatures,
                },
            });

            map.addLayer({
                id: "schools-points-layer",
                type: "circle",
                source: "schools-points",
                paint: {
                    "circle-color": "#FF0000",
                    "circle-radius": 3,
                },
            });

            map.addLayer({
                id: "schools-polygons-layer",
                type: "fill",
                source: "schools-polygons",
                paint: {
                    "fill-color": "#FF0000",
                    "fill-opacity": 1,
                },
            }, 'schools-points-layer');
        } catch (error) {
            console.error('Error adding school layers:', error);
        }
    };

    if (map.isStyleLoaded()) {
        addLayers();
    } else {
        map.on('style.load', () => {
            addLayers();
        });
    }
};
